import React from 'react';
import { Link } from 'react-router-dom';
import './Legalpages.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="legal-last-updated">Last Updated: April 30, 2026</p>

        <section className="legal-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using FocusView.ai ("Service," "we," "our," "us") at https://www.focusview.ai,
            you ("User," "you") agree to be bound by these Terms of Service ("Terms"). If you do not agree,
            do not use the Service.
          </p>
          <p>
            We reserve the right to update these Terms at any time. Continued use of the Service after
            changes are posted constitutes acceptance of the revised Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            FocusView.ai is a news aggregation platform that rephrases and curates publicly available news articles
            using artificial intelligence. Users may optionally create accounts to earn Dogecoin (DOGE) loyalty
            rewards for reading articles on the platform.
          </p>
          <p>
            We are not a news publisher, financial institution, cryptocurrency exchange, or money services business.
            Content on this platform is informational only and does not constitute financial, legal, or investment advice.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Eligibility</h2>
          <p>
            You must be at least 18 years old (or the age of majority in your jurisdiction) to create an account
            or participate in the rewards program. By registering, you represent that you meet this requirement.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. User Accounts</h2>
          <ul>
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            <li>You agree to provide accurate and complete registration information.</li>
            <li>You are responsible for all activity that occurs under your account.</li>
            <li>You may not create multiple accounts to circumvent reward limits or restrictions.</li>
            <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Dogecoin Reading Rewards Program</h2>

          <h3>5.1 How Rewards Work</h3>
          <p>
            Registered users earn a small DOGE loyalty reward for each unique article read. The current reward rate is displayed in your account dashboard and is subject to change.
            Duplicate reads of the same article do not yield additional rewards.
            Rewards are tracked as an account balance and are not transferable to other accounts.
          </p>

          <h3>5.2 Rewards Are Not Currency or Securities</h3>
          <p>
            DOGE reading rewards are a <strong>promotional loyalty program</strong> provided at our sole discretion.
            They are not wages, compensation, an investment return, a security, or a financial instrument.
            We make no representation regarding the monetary value of rewards at any time.
            Participation in the rewards program does not create any employment, partnership, or agency relationship.
          </p>

          <h3>5.3 Withdrawals</h3>
          <p>
            Users may request a DOGE withdrawal once their account balance reaches the minimum threshold (displayed in your account dashboard).
            A valid Dogecoin wallet address must be provided to initiate a withdrawal. Your wallet address is saved permanently upon your first confirmed withdrawal and cannot be changed through the self-service interface.
            Withdrawals are available only to users in eligible countries (displayed in the withdrawal screen). Eligible countries are determined at our sole discretion and may change. Your country is determined by your IP address at registration.
            Withdrawals are processed manually. When you submit a withdrawal request, our platform sends an automated email notification to the platform administrator, who will review and process your DOGE payment to your registered wallet address at their discretion. Withdrawals may take <strong>10–15 business days</strong> to process.
            We reserve the right to delay or deny withdrawals if we suspect fraud, abuse, or violations of these Terms.
          </p>
          <p>
            <strong>Withdrawal Cooldown:</strong> A 30-day waiting period applies between withdrawal requests. You may not submit a new withdrawal request until your previous request has cleared the cooldown period.
          </p>
          <p>
            <strong>Security Verification:</strong> Withdrawal requests require completion of a Cloudflare Turnstile security challenge to confirm you are a human user.
          </p>

          <h3>5.4 Program Changes and Termination</h3>
          <p>
            We may modify, suspend, or terminate the rewards program at any time with or without notice.
            Upon termination of the program, any outstanding balances will be honored for a reasonable period
            as determined by us. Accrued rewards are forfeited upon account termination for Terms violations.
          </p>

          <h3>5.5 Taxes</h3>
          <p>
            You are solely responsible for determining and paying any taxes applicable to rewards you receive.
            We do not provide tax advice and recommend consulting a qualified tax professional.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use bots, scripts, automated tools, or any artificial means to generate fake article reads or inflate reward balances.</li>
            <li>Create multiple accounts to circumvent reward limits or exploit the rewards program.</li>
            <li>Attempt to bypass or circumvent rate limiting, CAPTCHA, or other security measures.</li>
            <li>Attempt to hack, reverse-engineer, or interfere with the Service.</li>
            <li>Post or transmit harmful, defamatory, infringing, or illegal content.</li>
            <li>Impersonate any person or entity.</li>
            <li>Use the Service for any unlawful purpose.</li>
          </ul>
          <p>
            Violation of these prohibitions may result in immediate account termination and forfeiture of any
            accumulated rewards, in addition to any legal remedies available to us.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Intellectual Property</h2>
          <p>
            News articles are sourced from third-party publishers and are rephrased by AI. Original articles remain
            the property of their respective publishers. FocusView.ai's platform design, code, branding, and
            AI-generated summaries are our intellectual property. You may not copy, reproduce, or distribute
            our platform content without written permission.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            ACCURACY, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE.
          </p>
          <p>
            AI-generated article summaries may contain errors or inaccuracies. Always verify important information
            from original sources.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, FOCUSVIEW.AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING
            FROM YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF THESE TERMS SHALL NOT EXCEED THE GREATER OF
            (A) $10.00 USD OR (B) THE AMOUNT OF DOGE REWARDS YOU HAVE WITHDRAWN IN THE 90 DAYS PRECEDING THE CLAIM.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless FocusView.ai and its operators from any claims,
            damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the Service,
            your violation of these Terms, or your violation of any third-party rights.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of Florida, United States, without regard to its
            conflict-of-law principles. Any disputes shall be resolved in the state or federal courts located
            in Florida, and you consent to exclusive jurisdiction in those courts.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Termination</h2>
          <p>
            We may terminate or suspend your account at any time, with or without cause or notice.
            You may terminate your account at any time by using the <strong>Delete Account</strong> option within the app or website settings. Account deletion is immediate and permanent: all account data, reward balances, and earned loyalty rewards are irreversibly forfeited upon deletion. Any pending withdrawal requests are also cancelled.
            You may also terminate your account by contacting us at{' '}
            <a href="mailto:admin@focusview.ai">admin@focusview.ai</a>.
            Provisions of these Terms that by their nature should survive termination shall survive,
            including Sections 7, 8, 9, 10, and 11.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Content and Political Neutrality</h2>
          <p>
            FocusView aggregates and rephrases publicly available news articles across categories including cryptocurrency, artificial intelligence, and politics. All political content is sourced from established news outlets and presented for informational purposes only.
          </p>
          <p>
            <strong>FocusView does not endorse, promote, or express any political opinion, candidate, party, or ideology.</strong> We do not generate original political commentary. AI-rephrased summaries are editorial paraphrases of factual news reporting, not opinion or advocacy.
          </p>
          <p>
            Content on this platform may reference real-world events including elections, legislation, international conflicts, and geopolitical developments. Such references are journalistic in nature. Users are encouraged to seek multiple sources and form their own opinions.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Cryptocurrency Wallet and Reward Payments</h2>
          <p>
            To receive a DOGE loyalty reward payout, users may optionally provide a Dogecoin wallet address. Providing a wallet address is not required to use the Service or to accumulate rewards.
          </p>
          <p>
            <strong>Important:</strong> FocusView does not operate as a cryptocurrency exchange, broker, or automated payment processor. Reward payouts are <strong>voluntary, discretionary, and manually fulfilled</strong> by the platform administrator. Submitting a withdrawal request sends an email notification to the administrator, who reviews and decides whether to fulfill the payment at their sole discretion. No automated or guaranteed cryptocurrency transfer occurs.
          </p>
          <p>
            We do not take custody of, hold, or transmit Dogecoin on behalf of users. Your wallet address is stored solely for payout communication purposes. We are not responsible for errors in wallet addresses provided by users.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Contact Us</h2>
          <p>For questions about these Terms, contact us at:</p>
          <address>
            FocusView.ai<br />
            Email: <a href="mailto:admin@focusview.ai">admin@focusview.ai</a><br />
            Website: <a href="https://www.focusview.ai">https://www.focusview.ai</a>
          </address>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
